import type { BudgetCheckResult } from '../budget.js';
import type {
  CustomBreakdown,
  CustomBreakdownInput,
  DimensionSplit,
  DimensionSplitInput,
  SegmentSplit,
  SegmentSplitInput,
} from '../classification.js';
import type {
  AddressType,
  ApBillStatus,
  ApRecurrenceValueType,
  ApRecurrencyFrequency,
  ApRecurringBillType,
  BankAccountType,
  PaymentMethodType,
  PaymentPriority,
  PixKeyType,
  RiskRating,
  RowStatus,
  TaxIdType,
  TaxRegime,
  VendorDocumentStatus,
  VendorStatus,
  VendorType,
} from '../generated/enums.js';
import type { PartyBalanceSummary } from '../reconciliation/party-balance.js';

export interface Vendor {
  vendorId: string;
  name: string;
  email?: string;
  active: boolean;
}

/**
 * Vendor response for API/Web
 * Mirrors core Vendor fields needed by the UI.
 */
export interface VendorResponse {
  vendorId: string;
  vendorCode: string;
  legalName: string;
  tradeName?: string | null;
  status: VendorStatus;
  vendorType: VendorType;
  /** Whether this vendor is a collaborator (employee or contractor) for data-confidentiality purposes. */
  isCollaborator: boolean;
  lastTransactionDate?: string | null;

  // Tax & Legal
  taxIdType: TaxIdType;
  cnpj?: string | null;
  cpf?: string | null;
  /** ISO 3166-1 alpha-2 country for the vendor's legal identity. */
  countryCode?: string | null;
  /** Canonical foreign Tax ID for INTERNATIONAL, RFC, or EIN vendors. */
  foreignTaxId?: string | null;
  cnpjRoot?: string | null;
  establishmentType?: 'HEADQUARTERS' | 'BRANCH' | null;
  unifyGroupAccounts: boolean;
  stateRegistration?: string | null;
  ieExempt: boolean;
  municipalRegistration?: string | null;
  taxRegime: TaxRegime;
  suframaRegistration?: string | null;
  isFinalConsumer: boolean;
  icmsContributor: boolean;
  legalNatureCode?: string | null;
  optanteSimples?: boolean | null;
  optanteMei?: boolean | null;
  simplesNacionalValidatedAt?: string | null;
  simplesNacionalPeriods?: { dataInicio: string; dataFim?: string | null }[] | null;

  // Contact
  primaryContactName?: string | null;
  primaryContactEmail?: string | null;
  primaryContactPhone?: string | null;
  financialContactName?: string | null;
  financialContactEmail?: string | null;
  financialContactPhone?: string | null;
  website?: string | null;

  // Payment Terms
  defaultPaymentTermId?: string | null;
  paymentMethod?: PaymentMethodType | null;
  creditLimit?: string | null; // Decimal as string
  creditLimitCurrency: string;
  paymentPriority?: PaymentPriority | null;

  // Tax Configuration
  defaultTaxProfileId?: string | null;
  glOffsetAccountId?: string | null;
  glSupplierAdvanceAccountId?: string | null;

  // Fiscal Classification
  defaultCfop?: string | null;
  defaultNcm?: string | null;
  defaultCstIcms?: string | null;
  defaultCstIpi?: string | null;
  defaultCstPis?: string | null;
  defaultCstCofins?: string | null;

  // Additional
  vendorCategoryId?: string | null;
  riskRating?: RiskRating | null;
  performanceScore?: number | null;
  certifications?: Record<string, unknown> | null; // JSON
  notes?: string | null;
  customFields?: Record<string, unknown> | null; // JSON

  // AI & Classification (consistent with Department/CostCenter pattern)
  aiContext?: string | null;
  isMECE?: boolean;
  requiresMigrationIfInactivated?: boolean;

  // Timestamps
  createdAt: string;
  updatedAt?: string | null;
  confidentialDataRestricted?: boolean;

  /**
   * Optional embedded party balance summary. Populated only when the caller
   * requests it via `?include=partyBalance`. Does NOT appear on list endpoints.
   */
  partyBalance?: PartyBalanceSummary | null;
}

/**
 * Canonical request payload for creating vendors
 */
export interface CreateVendorRequest {
  vendorCode: string;
  legalName: string;
  tradeName?: string;
  status: VendorStatus;
  vendorType: VendorType;
  /** Classifies a contractor/PJ or employee vendor as a collaborator. This is separate from payroll enrollment. */
  isCollaborator?: boolean;
  /** Entity in which a collaborator's HCM employee record is created. It is not persisted on the vendor. */
  collaboratorEntityId?: string;
  taxIdType: TaxIdType;
  cnpj?: string;
  cpf?: string;
  /** ISO 3166-1 alpha-2 country for the vendor's legal identity. */
  countryCode?: string;
  /** Canonical foreign Tax ID value when taxIdType is INTERNATIONAL, RFC, or EIN. */
  foreignTaxId?: string;
  stateRegistration?: string;
  ieExempt?: boolean;
  municipalRegistration?: string;
  taxRegime: TaxRegime;
  suframaRegistration?: string;
  isFinalConsumer?: boolean;
  icmsContributor?: boolean;
  legalNatureCode?: string;

  /** When true (default), recurring bill accounts from all vendors sharing the same CNPJ root are considered during AP ingestion. */
  unifyGroupAccounts?: boolean;

  // Contact
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  financialContactName?: string;
  financialContactEmail?: string;
  financialContactPhone?: string;
  website?: string;

  // Payment Terms
  defaultPaymentTermId?: string;
  paymentMethod?: PaymentMethodType;
  creditLimit?: string;
  creditLimitCurrency?: string;
  paymentPriority?: PaymentPriority;

  // Tax Configuration
  defaultTaxProfileId?: string;

  // Fiscal Classification
  defaultCfop?: string;
  defaultNcm?: string;
  defaultCstIcms?: string;
  defaultCstIpi?: string;
  defaultCstPis?: string;
  defaultCstCofins?: string;

  // Additional
  vendorCategoryId?: string;
  riskRating?: RiskRating;
  performanceScore?: number;
  certifications?: Record<string, unknown>;
  notes?: string;
  customFields?: Record<string, unknown>;

  // AI & Classification (consistent with Department/CostCenter pattern)
  aiContext?: string;
  isMECE?: boolean;
  requiresMigrationIfInactivated?: boolean;
}

export type UpdateVendorRequest = Partial<CreateVendorRequest>;

/**
 * Optional vendor field values applied to the merge target after re-pointing FKs.
 * Same shape as partial vendor updates (API / persistence uses Prisma enums on the backend).
 *
 * Trade name and contact fields additionally accept `null`: the wizard resolves
 * these from a chosen source vendor's row, which may legitimately hold null, and
 * that null has to overwrite the target rather than being dropped as "not selected".
 * `undefined` still means "field not selected, leave the target's value alone".
 */
export type VendorMergeFieldOverrides = Omit<
  Partial<CreateVendorRequest>,
  | 'tradeName'
  | 'primaryContactName'
  | 'primaryContactEmail'
  | 'primaryContactPhone'
  | 'financialContactName'
  | 'financialContactEmail'
  | 'financialContactPhone'
> & {
  tradeName?: string | null;
  primaryContactName?: string | null;
  primaryContactEmail?: string | null;
  primaryContactPhone?: string | null;
  financialContactName?: string | null;
  financialContactEmail?: string | null;
  financialContactPhone?: string | null;
};

/**
 * Wizard-only selections applied inside `mergeVendorsCore`'s transaction.
 * Core callers omit this; when present, the keep-list wins over duplicate-skip.
 */
export interface VendorMergeSelection {
  bankAccountIdsToKeep?: string[];
  primaryAddressId?: string;
}

/**
 * Summary returned after merging one or more source vendors into a target vendor.
 */
export interface VendorMergeResult {
  targetVendorId: string;
  sourceVendorIds: string[];
  warnings: string[];
  billsVendorIdUpdated: number;
  billsPaymentBeneficiaryVendorIdUpdated: number;
  vendorAddressesReparented: number;
  vendorBankAccountsReparented: number;
  vendorBankAccountsSkippedDuplicate: number;
  vendorDocumentsReparented: number;
  vendorRecurringBillsReparented: number;
  reimbursementRequestsRequesterVendorIdUpdated: number;
  reconciliationAdjustmentsVendorIdUpdated: number;
  bankFeeConfigurationsVendorIdUpdated: number;
  vendorPccAccumulatorsReparented: number;
  vendorPccAccumulatorsConsolidated: number;
  glJournalLinesCounterpartyIdUpdated: number;
  partyMergePerformed: boolean;
  mergeLogIds: string[];
}

/**
 * Legacy aliases kept for backwards compatibility
 */
export type VendorRequest = CreateVendorRequest;

/** The vendor listing's sortable columns. */
export const VENDOR_SORT_COLUMNS = [
  'name',
  'status',
  'taxIdType',
  'document',
  'vendorType',
  'establishmentType',
  'email',
  'phone',
] as const;
export type VendorSortColumn = (typeof VENDOR_SORT_COLUMNS)[number];

/** The establishment filter's values: the enum, plus `NONE` for a vendor with none. */
export const VENDOR_ESTABLISHMENT_FILTER_VALUES = ['HEADQUARTERS', 'BRANCH', 'NONE'] as const;
export type VendorEstablishmentFilter = (typeof VENDOR_ESTABLISHMENT_FILTER_VALUES)[number];

/** Vendors per status for a listing's status views, plus `all`. */
export type VendorStatusCounts = Record<VendorStatus | 'all', number>;

export interface FindVendorsQuery {
  status?: VendorStatus; // Filter by status
  vendorType?: VendorType; // Filter by vendor type
  taxIdType?: TaxIdType; // Filter by tax ID type
  /** Several statuses (a listing column filter); takes precedence over `status`. */
  statusIn?: VendorStatus[];
  /** Several vendor types; takes precedence over `vendorType`. */
  vendorTypeIn?: VendorType[];
  /** Several tax ID types; takes precedence over `taxIdType`. */
  taxIdTypeIn?: TaxIdType[];
  /** Several establishment types; `NONE` matches a vendor with none. */
  establishmentTypeIn?: VendorEstablishmentFilter[];
  /** CNPJ/CPF or foreign tax ID contains this, punctuation ignored. */
  document?: string;
  /** Primary contact e-mail contains this, case-insensitive. */
  email?: string;
  /** Primary contact phone contains these digits, punctuation ignored. */
  phone?: string;
  /** Server-side sort of a listing; `name` is the legal name. Then by name. */
  sortBy?: VendorSortColumn;
  sortDirection?: 'asc' | 'desc';
  search?: string; // Search query (name, email, document)
  /** When set, returns only these vendor IDs (still tenant-scoped). */
  vendorIds?: string[];
  /**
   * Narrow to collaborator vendors, or explicitly exclude them.
   *
   * Classification decides whether a vendor's financial data is concealed from
   * anyone without payroll access, so the set has to be reviewable rather than
   * discoverable one record at a time.
   */
  isCollaborator?: boolean;
  page?: number;
  limit?: number;
}

/** Agent-friendly vendor summary returned by MCP read tools. */
export interface McpVendorSummaryResponse {
  vendorId: string;
  vendorCode: string;
  legalName: string;
  tradeName: string | null;
  status: VendorStatus | string;
  vendorType: VendorType | string;
  taxIdType: TaxIdType | string;
  cnpj: string | null;
  cpf: string | null;
  primaryContactEmail: string | null;
}

export interface PaginatedMcpVendorsResponse {
  data: McpVendorSummaryResponse[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Optional cherry-picks when merging vendors (conflicting or empty fields on the target).
 */
export interface MergeVendorsFieldOverrides {
  tradeName?: string;
  legalName?: string;
  vendorCode?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  financialContactName?: string;
  financialContactEmail?: string;
  financialContactPhone?: string;
  website?: string;
  notes?: string;
}

/**
 * Request body for the low-level vendor merge (used internally by VendorMergeService).
 */
export interface MergeVendorsCoreRequest {
  targetVendorId: string;
  sourceVendorIds: string[];
  fieldOverrides?: MergeVendorsFieldOverrides;
}

/**
 * Summary returned after a low-level vendor merge (VendorMergeService).
 */
export interface MergeVendorsCoreResult {
  targetVendorId: string;
  mergedVendorIds: string[];
  billsReassigned: number;
  bankAccountsMerged: number;
  addressesMerged: number;
  contactsMerged: number;
  /** Compatibility counter; posted journal counterparties are immutable, so this is always zero. */
  journalLinesUpdated: number;
}

/**
 * Vendor Address response for frontend use
 * Represents a vendor's address (billing, shipping, etc.)
 */
export interface VendorAddressResponse {
  addressId: string;
  vendorId: string;
  addressType: AddressType;
  isDefault: boolean;
  isActive: boolean;

  // Brazilian Address Fields
  street: string;
  number: string;
  complement?: string | null;
  neighborhood: string;
  city: string;
  state: string; // UF - 2 chars
  postalCode: string; // CEP
  ibgeCityCode?: string | null; // 7 digits
  country: string; // Default: BR
  serproValidated: boolean;

  // Timestamps
  createdAt: string;
  updatedAt?: string | null;
}

/**
 * Form data for creating vendor addresses (request payload)
 * Omits computed fields and IDs
 */
export type CreateVendorAddressRequest = Omit<
  VendorAddressResponse,
  | 'addressId'
  | 'vendorId'
  | 'createdAt'
  | 'updatedAt'
  | 'serproValidated'
  | 'isDefault'
  | 'isActive'
  | 'country'
> & {
  isDefault?: boolean;
  isActive?: boolean;
  country?: string;
};

/**
 * Form data for updating vendor addresses (request payload)
 * All fields optional except IDs
 */
export type UpdateVendorAddressRequest = Partial<CreateVendorAddressRequest>;

export interface VendorDocumentTypeDto {
  documentTypeId: string;
  typeCode: string;
  typeName: string;
  description: string | null;
  isMandatory: boolean;
  requiresExpiryDate: boolean;
  validityPeriodDays: number | null;
  regulatoryRequirement: boolean;
  allowedFileTypes: Record<string, unknown> | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface VendorDocumentDto {
  vendorDocumentId: string;
  vendorId: string;
  documentTypeId: string;
  apDocumentId: string;
  documentNumber: string | null;
  documentName: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  issuingAuthority: string | null;
  verificationCode: string | null;
  status: VendorDocumentStatus;
  verified: boolean;
  verifiedBy: string | null;
  verifiedDate: string | null;
  nextReviewDate: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  documentType: VendorDocumentTypeDto;
  apDocument: {
    entityId: string;
    originalName: string;
    mimeType: string;
    fileSizeBytes: number;
    blobUrl: string | null;
  };
}

/**
 * Vendor Bank Account response for frontend use
 * Represents a vendor's bank account information including PIX configuration
 */
export interface VendorBankAccountResponse {
  bankAccountId: string;
  vendorId: string;
  isDefault: boolean;
  isActive: boolean;
  accountStatus?: string | null;

  // Bank Information
  bankCode?: string | null;
  bankName?: string | null;
  agency?: string | null;
  agencyDigit?: string | null;
  accountNumber?: string | null;
  accountDigit?: string | null;
  accountType?: BankAccountType | null;
  accountHolderName?: string | null;

  // US (ACH) accounts: `country` is `US`, with routing number and account class.
  // Brazilian accounts keep `country` `BR` (the default) and use the bank fields above.
  country?: string;
  routingNumber?: string | null;
  accountClass?: 'CHECKING' | 'SAVINGS' | null;

  // True once the account is registered as an instrument at the payment gateway. Its routing
  // and account numbers are then frozen: changing them needs a new account.
  registeredAtGateway?: boolean;

  // PIX Configuration
  pixEnabled: boolean;
  pixKeyType?: PixKeyType | null;
  pixKey?: string | null;

  // Verification
  verified: boolean;
  verifiedDate?: string | null;
  verifiedBy?: string | null;
  verificationObservation?: string | null;
  verifiedByUser?: {
    userId: string;
    name: string;
    email: string;
  } | null;

  // Timestamps
  createdAt: string;
  updatedAt?: string | null;
}

/**
 * Form data for creating vendor bank accounts (request payload)
 * Omits computed fields and IDs
 */
export type CreateVendorBankAccountRequest = Omit<
  VendorBankAccountResponse,
  | 'bankAccountId'
  | 'vendorId'
  | 'createdAt'
  | 'updatedAt'
  | 'verifiedDate'
  | 'verifiedBy'
  | 'verifiedByUser'
  | 'registeredAtGateway'
  | 'verified'
  | 'verificationObservation'
  | 'isDefault'
  | 'isActive'
  | 'pixEnabled'
> & {
  isDefault?: boolean;
  isActive?: boolean;
  pixEnabled?: boolean;
  /** Optional caller retry key, bound to one canonical destination request per tenant. */
  idempotencyKey?: string;
};

/**
 * Form data for updating vendor bank accounts (request payload)
 * All fields optional except IDs
 */
/** PATCH must not accept the create-only retry key. */
export type UpdateVendorBankAccountRequest = Partial<
  Omit<CreateVendorBankAccountRequest, 'idempotencyKey'>
>;

export interface VerifyVendorBankAccountRequest {
  observation: string;
}

/**
 * Vendor payment method usage statistics
 * Used to display smart recommendations in payment creation forms
 */
export interface VendorPaymentStatsResponse {
  paymentMethod: PaymentMethodType;
  usageCount: number;
  lastCreatedDate: string | null; // ISO date string (YYYY-MM-DD) - date when payment was created
  preferenceScore: number; // 0-100 score based on frequency and recency
  isDefault: boolean; // Whether this is the vendor's default payment method

  // Payment method details (for 1-click payment creation)
  pixKey?: string | null; // PIX key for PIX payments
  bankAccountId?: string | null; // Bank account ID for TED/DOC payments
  bankName?: string | null; // Bank name for display
  bankAccount?: string | null; // Formatted account number for display (agency-digit + account-digit)
}

export type RecurrencyFrequency = ApRecurrencyFrequency;
export type RecurrenceValueType = ApRecurrenceValueType;
/**
 * Business nature of the template (`STANDARD` | `PAYROLL`), orthogonal to
 * {@link RecurrenceValueType}, which describes only how the amount behaves.
 * `PAYROLL` places the template and every bill it materializes inside the
 * payroll-confidentiality boundary.
 */
export type RecurringBillType = ApRecurringBillType;

export const RECURRING_COMMITMENT_RESOLUTION_OPTIONS = ['KEEP_OPEN', 'CLEAR_REMAINDER'] as const;
export type RecurringCommitmentResolution =
  (typeof RECURRING_COMMITMENT_RESOLUTION_OPTIONS)[number];

export interface RecurringBillTemplateClassificationRequest {
  allocationType?: 'SIMPLE' | 'CUSTOM_BREAKDOWN';
  glAccounts: DimensionSplitInput[];
  departments?: DimensionSplitInput[];
  costCenters?: DimensionSplitInput[];
  managerialAccounts?: DimensionSplitInput[];
  segments?: SegmentSplitInput[];
  customBreakdown?: CustomBreakdownInput;
}

export interface RecurringBillTemplateClassificationResponse {
  classificationId: string;
  allocationType?: 'SIMPLE' | 'CUSTOM_BREAKDOWN';
  glAccounts: DimensionSplit[];
  departments?: DimensionSplit[];
  costCenters?: DimensionSplit[];
  managerialAccounts?: DimensionSplit[];
  segments?: SegmentSplit[];
  customBreakdown?: CustomBreakdown;
}

export interface VendorRecurringBillOccurrence {
  billId: string;
  dueDate: string | null;
  /** Net payable amount for the bill (aligned with bill detail "valor líquido"). */
  netPaymentAmount: number | null;
  /** Competency period for the bill (ISO date string). */
  billCompetencyDate: string | null;
  totalAmount: number | null;
  currencyCode: string | null;
  status: ApBillStatus;
  installmentNumberRecurringBill?: number | null;
}

export interface VendorRecurringBillResponse {
  /** Manual cash-date offset from competency; null restores inference. */
  paymentTermDays?: number | null;
  inferredPaymentTerm?: import('./cash-date.js').RecurringPaymentTermEstimate | null;
  tenantId: string;
  vendorRecurringBillId: string;
  vendorId: string;
  entityId: string;
  status: RowStatus;
  name: string;
  description?: string | null;
  descriptionNormalized?: string | null;
  currencyCode: string;
  amount?: number | null;
  periodLimitAmount: number;
  recurrenceType?: RecurrenceValueType | null;
  /** Defaults to `STANDARD`; `PAYROLL` marks the template as confidential. */
  recurringBillType: RecurringBillType;
  variableComparisonMonths?: number | null;
  recurrencyFrequency: RecurrencyFrequency;
  expectedDueDayOfMonth?: number | null;
  expectedCompetencyDayOfMonth?: number | null;
  recurringSince?: string | null; // ISO date string
  recurringUntil?: string | null; // ISO date string
  totalInstallments?: number | null;
  sourceVendorDocumentId?: string | null;
  /** Contract the template was prefilled from; null when absent or soft-deleted. */
  sourceVendorDocument?: {
    vendorDocumentId: string;
    documentName: string | null;
    apDocumentId: string;
  } | null;
  classification?: RecurringBillTemplateClassificationResponse | null;
  occurrences?: VendorRecurringBillOccurrence[];
  createdAt: string;
  updatedAt?: string | null;
}

/** Lightweight result used by type-ahead selectors. */
export interface VendorRecurringBillSearchResult {
  vendorRecurringBillId: string;
  vendorId: string;
  name: string;
  currencyCode: string;
  amount?: number | null;
  periodLimitAmount: number;
  recurringUntil?: string | null;
}

export interface CreateVendorRecurringBillRequest {
  paymentTermDays?: number | null;
  /**
   * Required for vendors that are not tied to a single entity.
   * When omitted, backend may fall back to vendor.entityId (if available).
   */
  entityId?: string;
  name: string;
  description?: string;
  currencyCode?: string;
  amount?: number;
  periodLimitAmount: number;
  classification: RecurringBillTemplateClassificationRequest;
  recurrenceType?: RecurrenceValueType | null;
  /**
   * Defaults to `STANDARD`. Setting `PAYROLL` immediately hides the template,
   * its occurrences and their GL/cash-flow traces from principals without
   * `payroll:view`.
   */
  recurringBillType?: RecurringBillType | null;
  variableComparisonMonths?: number | null;
  recurrencyFrequency?: RecurrencyFrequency;
  status?: RowStatus;
  expectedDueDayOfMonth?: number | null;
  expectedCompetencyDayOfMonth?: number | null;
  recurringSince?: string;
  recurringUntil?: string; // ISO date string
  totalInstallments?: number;
  /** Vendor contract (same tenant and vendor) the template was prefilled from. */
  sourceVendorDocumentId?: string;
  dueDay?: number;
  /**
   * When true, includes past recurring occurrences for budget commitments,
   * limited to OPEN periods inside the active budget plan window.
   */
  includeRetroactivePeriods?: boolean;
}

/** Vendor document type code whose uploads can prefill a recurring bill. */
export const VENDOR_CONTRACT_DOCUMENT_TYPE_CODE = 'CONTRACT';

/** Unsaved recurring-bill fields extracted from a vendor contract; null = not found. */
export interface VendorContractRecurringBillDraft {
  name: string | null;
  description: string | null;
  currencyCode: string | null;
  amount: number | null;
  recurrenceType: RecurrenceValueType | null;
  recurrencyFrequency: RecurrencyFrequency | null;
  expectedDueDayOfMonth: number | null;
  recurringSince: string | null;
  recurringUntil: string | null;
  totalInstallments: number | null;
}

export interface VendorContractRecurringBillDraftResponse {
  sourceVendorDocumentId: string;
  draft: VendorContractRecurringBillDraft;
}

export type UpdateVendorRecurringBillRequest = Omit<
  Partial<CreateVendorRecurringBillRequest>,
  | 'recurringUntil'
  | 'totalInstallments'
  | 'periodLimitAmount'
  | 'classification'
  | 'sourceVendorDocumentId'
> & {
  recurringSince?: string | null;
  recurringUntil?: string | null;
  totalInstallments?: number | null;
  periodLimitAmount?: number;
  classification?: RecurringBillTemplateClassificationRequest;
};

export interface RecurringCommitmentSyncResult {
  includeRetroactivePeriods: boolean;
  includedRetroactivePeriods: string[];
  skippedClosedPeriods: string[];
}

export interface RecurringTemplateClassificationSyncResult {
  updatedBillsCount: number;
}

export type RecurringBudgetGateStatus = 'ok' | 'gate_failed' | 'sync_failed';

export interface VendorRecurringBillMutationResponse extends VendorRecurringBillResponse {
  budgetCheck?: BudgetCheckResult | null;
  commitmentSync?: RecurringCommitmentSyncResult | null;
  budgetGateStatus?: RecurringBudgetGateStatus;
  classificationSync?: RecurringTemplateClassificationSyncResult | null;
}

export interface MergeVendorRecurringBillsRequest {
  keepVendorRecurringBillId: string;
  mergeVendorRecurringBillIds: string[];
}

export interface MergeVendorRecurringBillsResponse {
  keepVendorRecurringBillId: string;
  mergedVendorRecurringBillIds: string[];
  reassignedBillsCount: number;
}

export interface VendorRecurringLinkableBillResponse {
  billId: string;
  billCompetencyDate: string | null;
  dueDate: string | null;
  totalAmount: number | null;
  currencyCode: string | null;
  billStatus: ApBillStatus;
  linkedVendorRecurringBillId: string | null;
  linkedVendorRecurringBillName: string | null;
  isLinkedToSelectedTemplate: boolean;
}

export interface ListVendorRecurringLinkableBillsResponse {
  vendorRecurringBillId: string;
  bills: VendorRecurringLinkableBillResponse[];
}

export interface UpdateVendorRecurringBillLinksRequest {
  linkBillIds: string[];
  unlinkBillIds: string[];
}

export interface UpdateVendorRecurringBillLinksResponse {
  vendorRecurringBillId: string;
  linkedCount: number;
  unlinkedCount: number;
  reassignedCount: number;
}

/**
 * Merge multiple vendor records into a single target vendor.
 * All `vendorIds` must belong to the same tenant; `targetVendorId` must be included.
 */
export interface MergeVendorsRequest {
  targetVendorId: string;
  /** Every vendor participating in the merge (including the target), minimum 2 IDs. */
  vendorIds: string[];
  /** Which vendor's trade name to persist on the target (must be one of `vendorIds`). */
  tradeNameFromVendorId: string;
  /** Bank account row IDs to keep when merged vendors have bank accounts. */
  bankAccountIdsToKeep?: string[];
  /** Address row ID that becomes the primary (`isDefault`) on the merged vendor when addresses exist. */
  primaryAddressId?: string;
  /** Which vendor's primary + financial contact fields to copy onto the target. */
  contactFromVendorId: string;
  /**
   * Promote an `UNDER_REVIEW` target to `ACTIVE` as part of the merge.
   * Opt-in only, and only for `UNDER_REVIEW`: an `INACTIVE` or `BLOCKED` vendor is blocked
   * deliberately and is never reactivated as a side effect of a merge.
   */
  activateTarget?: boolean;
}

/** Successful merge outcome with counts for UI feedback. */
export interface MergeVendorsResponse {
  targetVendorId: string;
  billsReassigned: number;
  bankAccountsConsolidated: number;
  vendorsDeactivated: number;
  totalBilledAmountReassigned: string;
  /** True when the merge promoted the target from `UNDER_REVIEW` to `ACTIVE`. */
  targetActivated: boolean;
  /**
   * Non-fatal notes about what the merge could not fold in (a skipped duplicate tax
   * identifier, a source that was already inactive). The merge succeeded; these are for
   * the operator to follow up on.
   */
  warnings: string[];
}

/**
 * Why a vendor record cannot be absorbed as a merge source.
 *
 * These are the conditions `mergeVendors` refuses on. The preview reports them per vendor so
 * the wizard can point at the offending row up front instead of failing on the final confirm.
 */
export type VendorMergeBlockerCode =
  'BANK_FEE_CONFIGURATION' | 'REIMBURSEMENT_REQUESTER' | 'PCC_ACCUMULATOR';

/** Enrichment for vendors already known to the client (list selection). */
export interface MergeVendorPreviewRow {
  vendorId: string;
  status: VendorStatus;
  /** Whether this record may survive the merge (`ACTIVE` or `UNDER_REVIEW`). */
  canBeTarget: boolean;
  /** Conditions that stop this record from being merged away; empty when it can be. */
  sourceBlockers: VendorMergeBlockerCode[];
  billCount: number;
  totalBilledAmount: string;
  addresses: VendorAddressResponse[];
  bankAccounts: VendorBankAccountResponse[];
}

export interface MergeVendorsPreviewResponse {
  vendors: MergeVendorPreviewRow[];
}

/**
 * One vendor row inside a duplicate group (same normalized CNPJ/CPF).
 * Bill stats include only non-deleted bills linked to this vendor as `vendor_id`.
 */
export interface VendorDuplicateMember {
  vendorId: string;
  code: string;
  name: string;
  status: VendorStatus;
  billCount: number;
  /** Sum of `total_amount` on linked bills (0 when null amounts). */
  totalBilled: string;
}

/**
 * Group of vendors sharing the same digit-only tax id (CNPJ or CPF).
 */
export interface VendorDuplicateGroup {
  normalizedTaxId: string;
  vendors: VendorDuplicateMember[];
  /** Suggested merge target: highest bill count, then ACTIVE status, then stable tie-breakers. */
  suggestedTargetVendorId: string;
}

export interface VendorDuplicatesResponse {
  groups: VendorDuplicateGroup[];
}

/**
 * A group of vendors with similar legal names (pg_trgm similarity).
 * Used by the duplicate detection UI to surface likely duplicates for merging.
 */
export interface VendorDuplicateGroupResponse {
  /** Longest legal name in the group, used as display label. */
  representativeName: string;
  /** Best pairwise similarity score within the group (0-100). */
  similarityScore: number;
  suggestedTargetVendorId: string;
  vendors: {
    vendorId: string;
    vendorCode: string;
    legalName: string;
    tradeName?: string | null;
    status: VendorStatus;
    /**
     * Conditions that stop this record from being merged away, so duplicate review can say so
     * before the operator confirms rather than relaying the endpoint's refusal afterwards.
     * Same codes the merge preview reports.
     */
    sourceBlockers: VendorMergeBlockerCode[];
    billCount: number;
    totalBilled: string;
    taxIdType: TaxIdType;
    cnpj?: string | null;
    cpf?: string | null;
  }[];
}

export interface DismissVendorDuplicateRequest {
  vendorIdA: string;
  vendorIdB: string;
}

export interface VendorMergeSourceSnapshot {
  vendorId?: string;
  legalName?: string;
  tradeName?: string | null;
  vendorCode?: string;
  cnpj?: string | null;
  cpf?: string | null;
  taxIdType?: TaxIdType;
}

export interface VendorMergeHistoryUserSummary {
  userId: string;
  name: string;
  email: string;
}

/**
 * Merge history entry for a vendor that absorbed another via merge.
 * Corresponds to rows in `apVendorMergeLog` with a snapshot of the source vendor.
 */
export interface VendorMergeHistoryEntryResponse {
  vendorMergeLogId: string;
  targetVendorId: string;
  performedByUserId: string;
  performedByName: string;
  performedByEmail: string;
  billsReassigned: number;
  sourceVendor: VendorMergeSourceSnapshot;
  createdAt: string;
}
